// Wallet → Credit relabel (GW-411).
//
// The OE Commerce storefront calls the customer's stored value "Wallet";
// Goldwagen brands it "Credit". This relabels the visible wallet wording on the
// CHECKOUT page and — crucially — in toast NOTIFICATIONS, which the previous
// (CMS content-zone) script never covered and which has since stopped working.
//
// Two jobs:
//   1. DOM: on the checkout page, rewrite "Wallet…" text nodes to "Credit…",
//      re-applying on the checkout summary's Mustache re-renders (apply wallet,
//      select shipping, …) via a MutationObserver.
//   2. Toasts: wrap the storefront's `Utils.Toastr({message,title})` so any
//      wallet wording in a notification reads "Credit" (e.g. the server's
//      "Wallet applied" / "Wallet can only be applied after selecting shipping").
//
// The account/nav menu keeps its own "Account Balance" wording (account-menu.js
// owns it), so the DOM pass deliberately SKIPS header/nav to avoid clobbering
// it. Never throws; no-ops off the checkout page (toast wrap stays, harmlessly).

import { getTopDocument } from '../shared/dom.js';

// Format a bare amount as Rand: "1209.99" / "33,957.62" → "R1,209.99" /
// "R33,957.62". Returns null when it isn't a parseable number (leave as-is).
function formatRand(numStr) {
  var n = parseFloat(String(numStr).replace(/,/g, ''));
  if (!isFinite(n)) return null;
  var fx = n.toFixed(2).split('.');
  fx[0] = fx[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return 'R' + fx[0] + '.' + fx[1];
}

// DOM relabel — wallet wording only. Longest phrases first so
// "Wallet Points Available" isn't half-replaced. Deliberately does NOT touch a
// bare "points": the checkout has legitimate "collection point(s)" wording. Also
// Rand-formats the balance shown right after a Credit label, but ONLY when it's
// in the same text node (idempotent — an already-"R" value won't re-match).
function relabelText(s) {
  return String(s == null ? '' : s)
    .replace(/wallet\s+points\s+applied/gi, 'Credit Applied')
    .replace(/wallet\s+points\s+available/gi, 'Credit Available')
    .replace(/wallet\s+points/gi, 'Credit')
    // Core V8.018.010 says "Wallet credit" ("Wallet credit available: …"), which
    // the bare-word swap below turned into "Credit credit".
    .replace(/wallet\s+credit/gi, 'Credit')
    .replace(/\bwallet\b/gi, 'Credit')
    .replace(/\bcredit\s+credit\b/gi, 'Credit')
    // Rand-format a bare balance after "Available:" / "Applied:" (with or without
    // a "Credit" prefix). Idempotent: an amount that already has "R" won't match.
    .replace(/((?:Credit\s+)?(?:Available|Applied)\s*:?\s*)(\d[\d,]*(?:\.\d+)?)/gi, function (_m, pre, num) {
      return pre + (formatRand(num) || num);
    });
}

// Text nodes worth a pass: wallet wording, or a bare credit amount after
// "Available:" / "Applied:" (core renders those as separate nodes).
var NEEDS_PASS = /allet|(?:Available|Applied)\s*:?\s*\d/i;

// Toast relabel — additionally handles bare "point(s)", because the server's
// wallet notifications say e.g. "Successfully applied 1209.99 points on your
// cart." with no "wallet" to anchor on. The client wants no "wallet"/"points"
// wording at all, and the amount as Rand. Safe here (unlike the DOM) because
// wallet apply/remove are the only toasts that talk about "points".
function relabelToast(s) {
  return relabelText(s)
    // "<amount> points" → "R<amount> credit"
    .replace(/(\d[\d,]*(?:\.\d+)?)\s*points?\b/gi, function (_m, num) {
      return (formatRand(num) || num) + ' credit';
    })
    // any remaining bare "point(s)" with no amount → "credit"
    .replace(/\bpoints?\b/gi, 'credit')
    .replace(/\bcredit\s+credit\b/gi, 'credit');
}

function currentPath() {
  try { return (window.top || window).location.pathname || ''; }
  catch (e) { try { return window.location.pathname || ''; } catch (_) { return ''; } }
}
function onCheckout() { return /(^|\/)checkout(\/|$)/i.test(currentPath()); }

// Skip subtrees the pass must not touch: the header/nav account menu (owned by
// account-menu.js), and non-visible-text hosts.
function skip(el) {
  if (!el) return true;
  try { return !!el.closest('header, nav, script, style, textarea, input, [data-parts-account-menu]'); }
  catch (e) { return false; }
}

function relabelDom() {
  var doc;
  try { doc = getTopDocument(); } catch (e) { return; }
  var root = doc && doc.body;
  if (!root) return;
  var walker;
  try { walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null); }
  catch (e) { return; }
  var n;
  while ((n = walker.nextNode())) {
    var v = n.nodeValue;
    if (!v || !NEEDS_PASS.test(v)) continue; // fast prefilter
    if (skip(n.parentElement)) continue;
    var next = relabelText(v);
    if (next !== v) n.nodeValue = next;
  }
}

// Wrap the storefront toast so wallet wording in notifications reads "Credit".
// Idempotent, and safe on any page (only rewrites messages containing "wallet").
function wrapToastr() {
  var U;
  try { U = (window.top || window).Utils; } catch (e) { U = window.Utils; }
  if (!U || typeof U.Toastr !== 'function') return false;
  if (U.Toastr.__pfCreditWrapped) return true;
  var orig = U.Toastr;
  var wrapped = function (opts) {
    try {
      if (opts && typeof opts === 'object') {
        if (typeof opts.message === 'string') opts.message = relabelToast(opts.message);
        if (typeof opts.title === 'string') opts.title = relabelToast(opts.title);
      }
    } catch (e) { /* ignore */ }
    return orig.apply(this, arguments);
  };
  wrapped.__pfCreditWrapped = true;
  try { U.Toastr = wrapped; } catch (e) { return false; }
  return true;
}

var scheduled = false;
function schedule() {
  if (scheduled) return;
  scheduled = true;
  var run = function () { scheduled = false; try { relabelDom(); } catch (e) { /* ignore */ } };
  if (window.requestAnimationFrame) window.requestAnimationFrame(run); else setTimeout(run, 16);
}

export function init() {
  // Toast wrap is global (harmless off-checkout). Utils loads with the storefront
  // scripts, which may be slightly after us — poll briefly until it's there.
  if (!wrapToastr()) {
    var tries = 0;
    var iv = setInterval(function () {
      if (wrapToastr() || ++tries > 40) clearInterval(iv); // ~10s ceiling
    }, 250);
  }

  // DOM relabel only on the checkout page (keeps the header account menu intact).
  if (!onCheckout()) return;
  try { relabelDom(); } catch (e) { /* ignore */ }
  try {
    var doc = getTopDocument();
    // childList/subtree catches the summary's Mustache re-renders (which replace
    // whole nodes) without the self-retrigger risk of characterData.
    new MutationObserver(schedule).observe(doc.documentElement || doc.body, { childList: true, subtree: true });
  } catch (e) { /* ignore */ }
}

export var id = 'checkout-credit';
